import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Headline } from '@xingternal/typography';

import { useRecommendedCompanies } from '@/hooks/useRecommendedCompanies';
import { useDeleteCompanyRecommendation } from '@/hooks/useDeleteCompanyRecommendation';
import { useTrackPage } from '@/hooks/useTrackPage';
import { useXingFrame } from '@/hooks/useXingFrame';
import { useCompanyData } from '@/contexts/CompaniesDataContext';

import { CompanyCardContainer } from '@/components/CompanyCardContainer';
import { CompanyCardsSkeletonLoggedin } from '@/components/Skeleton';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import { MetaData } from '@/components/MetaData';
import { PageError } from '@/components/PageError';
import { Company, Maybe, CompanyRecommendation } from '@/types/graphql-types';
import {
  pagesTrackingName,
  trackViewMoreRecommendedCompanies,
} from '@/utils/tracking';

import styles from './RecommendedCompanies.css';
import RecommendedCompanyCard from './RecommendedCompanyCard';
import { NetworkStatus } from '@apollo/client';

const RecommendedCompanies: React.FC = () => {
  const intl = useIntl();
  const {
    features: { isBrandManagerEditor },
  } = useXingFrame();
  const { addCompanies } = useCompanyData();

  const { data, networkStatus, loading, error, pagination } =
    useRecommendedCompanies();

  const [deletedCompanies, setDeletedCompanies] = useState<number>(0);

  const { handleDeleteRecommendation } = useDeleteCompanyRecommendation({
    setDeletedCompanies,
  });

  useTrackPage({ pageName: pagesTrackingName.recommended });

  const companiesRecommendations = useMemo(() => {
    if (!data?.viewer?.companiesRecommendations?.edges) return undefined;

    return (
      data?.viewer?.companiesRecommendations?.edges?.map(
        (item) => item?.node as Maybe<CompanyRecommendation>,
      ) as Maybe<CompanyRecommendation>[]
    ).filter((item) => item) as CompanyRecommendation[];
  }, [data?.viewer?.companiesRecommendations?.edges]);

  useEffect(() => {
    if (companiesRecommendations) {
      addCompanies({
        companies: companiesRecommendations.map(
          (item) => item?.company as Company,
        ),
      });
    }
  }, [companiesRecommendations]);

  const loadingFirstRender =
    loading && networkStatus !== NetworkStatus.fetchMore;
  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;

  const { hasNextPage, endCursor } =
    data?.viewer?.companiesRecommendations?.pageInfo ?? {};

  const isPaginationDisabled = () => loadingFirstRender;

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({ id: 'CP_RECOMMENDED_COMPANIES_HEADER' })}
        description={intl.formatMessage({
          id: 'CP_RECOMMENDED_COMPANIES_HEADER',
        })}
      />
      <Headline size="medium" className={styles.headline}>
        <FormattedMessage id="CP_RECOMMENDED_COMPANIES_HEADER" />
      </Headline>
      <CompanyCardContainer>
        {companiesRecommendations?.map((node) => (
          <RecommendedCompanyCard
            key={node.company?.id}
            recommendedCompany={node}
            onRemoveButtonClick={(company) => {
              handleDeleteRecommendation(company?.id);
            }}
          />
        ))}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => {
            pagination(endCursor, deletedCompanies, setDeletedCompanies);
            trackViewMoreRecommendedCompanies({
              userIsEditor: isBrandManagerEditor,
            });
          }}
          intlKey="CP_OTHER_INTERESTING_COMPANIES"
        />
      )}
    </>
  );
};

export { RecommendedCompanies };
